import React from 'react';
import { FILE, REPORT_DIALOG_STATE } from "./Types";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import FileList from "./FileList/FileList";
import _ from "lodash";
import ReactDOMServer from 'react-dom/server'
import { PromoteDemoteAbstract } from "../../../../Drawer/Tabs/Files/PromoteDemoteAbstract";
import { PuzzleTabStrip } from "puzzle";
import { FILES_STATE, PROPS } from "../../../../Drawer/Tabs/Types";

class ReportDialog extends PromoteDemoteAbstract<FILES_STATE, PROPS> {
  state: REPORT_DIALOG_STATE = {
    activeTabIndex: 0,
    files: {
      uploaded: [],
      failed: [],
      ignored: []
    },
    tabCount: 3,
    massSelectionConfiguration: {
      allClaimed: false,
      actionCannotBeReverted: false,
      availableEditors: null,
      demoteCommentRequired: false,
      demotionPhase: null,
      fileHasNoChanges: false,
      fileNotMarkAsCompleted: false,
      hasCompletedFiles: false,
      ipcEnabled: true,
      multiplePhasesSelected: false,
      multipleWorkflowsSelected: false,
      phaseType: '',
      promoteCommentRequired: false,
      promoteDemoteDisclaimer: false
    },
  };

  static defaultProps = {
    urlConfigs: {
      paramsConfig: {
        folder: 'FILE_STATUS_SEARCH'
      }
    }
  };

  moduleDidMount = () => {
    const { reportData } = this.props;
    let files = this.processFilesData(reportData);
    let activeTabIndex = 0;
    if (files.failed.length) {
      activeTabIndex = 1;
    } else if (files.ignored.length) {
      activeTabIndex = 2;
    }
    this.setState({ files, activeTabIndex });
  }

  /**
   * Count number of files
   */
  countFiles = (documents: FILE[]) => {
    let count = 0;
    let documentsLength = documents.length;
    let i = 0;
    let current;
    while (i < documentsLength) {
      current = documents[i];
      if (current.subItems && current.subItems.length) {
        count = count + current.subItems.length;
      } else {
        count++;
      }
      i++;
    }

    return count;
  };

  /**
   * @override
   */
  createRequestParams = (): { targetIds: number[] } => {
    return { targetIds: this.props.uploadedTargetIds }
  };

  /**
   * Generate validation text per error short code
   */
  generateValidationText = (file: FILE, prepareForJson: boolean) => {
    let validationText = '';
    if (file.fileUploadStatus === 'UPLOADED') {
      validationText = this.translate(`validationTextSuccess.${file.payload}`);
    } else if (file.fileUploadStatus === 'FAILED') {
      if (file.validationResult === 'EMPTY_SEGMENT_ERROR' && file.message) {
        const emptySegments = file.message.split(':')[1];
        validationText = this.translate(`validationText.${file.validationResult}`, {
          emptySegments,
          className: 'tp-text-semibold'
        });
      } else {
        validationText = this.translate(`validationText.${file.validationResult}`, { className: 'tp-text-semibold' })
      }
    }

    if (prepareForJson && _.isObject(validationText)) {
      validationText = ReactDOMServer.renderToString(validationText);
      let replaceRegex = /<span [^>]*>|<\/span>/g;
      validationText = validationText.replace(replaceRegex, '');
    }

    return validationText;
  };

  /**
   * Get params for complete action
   */
  getParamsForComplete = () => {
    let { submissionData, taskId } = this.props;
    if (!submissionData) {
      submissionData = {
        taskId
      }
    }
    return submissionData;
  }

  /**
   * Returns all uploaded files in one array for reopen dialog purpose
   */
  getUploadedFiles = () => {
    return { uploadReportsDto: _.flatten(this.props.reportData) };
  };

  /**
   * Get completeSupplierURL
   * @actionName
   */
  getCompleteSupplierURL = (actionName: 'promote' | 'demote') => {
    let endpoint = actionName === 'promote' ? 'complete' : 'return';
    return `/jobPhasesFiles/${this.props.taskId}/${endpoint}`;
  }

  /**
   * Change tab selection
   */
  onTabSelect = (event: TabStripSelectEventArguments) => {
    this.setState({
      activeTabIndex: event.selected
    });
  }

  /**
   * Prepare json for download report
   */
  prepareDownloadJson = () => {
    const { reportData: documents, submissionData } = this.props;
    let documentsLength = documents.length;
    let i = 0;
    let currentDocument;
    let targetCommandList = [];
    let sourceLanguage;
    while (i < documentsLength) {
      currentDocument = documents[i];
      let filesLength = currentDocument.length;
      let j = 0;
      let currentFile;
      let fileCommandList = [];
      while (j < filesLength) {
        currentFile = currentDocument[j];
        let validationText = this.generateValidationText(currentFile, true);
        fileCommandList.push({
          fileName: currentFile.fileName,
          message: validationText,
          payload: currentFile.payload,
          errorShortCode: currentFile.validationResult,
          isValid: currentFile.fileUploadStatus === 'UPLOADED'
        });
        j++;
      }
      sourceLanguage = currentFile.sourceLanguage;
      targetCommandList.push({
        batchName: currentFile.batchName,
        documentName: currentFile.documentName,
        phaseName: currentFile.phaseName,
        targetLanguage: currentFile.targetLanguageName?.split('[')[0].trim(),
        fileCommandList: fileCommandList
      })
      i++;
    }

    let downloadReportParams = {
      sourceLanguage: sourceLanguage,
      submissionId: submissionData.submissionId,
      submissionName: submissionData.submissionName,
      targetCommandList: targetCommandList
    }

    return downloadReportParams;
  }

  /**
   * Process initial data and prepare it for render
   */
  processFilesData = (documents: [FILE[]]) => {
    let documentsLength = documents.length;
    let i = 0;
    let currentDocument;
    let newItem;
    let results = {
      uploaded: [],
      failed: [],
      ignored: []
    }
    while (i < documentsLength) {
      currentDocument = documents[i];
      let filesLength = currentDocument.length;
      let j = 0;
      let currentFile;
      let subItems = [];
      let samePayload = true;
      let oldPayload;
      let haveSomeFailed = false;
      while (j < filesLength) {
        currentFile = currentDocument[j];
        if (!haveSomeFailed) {
          haveSomeFailed = currentFile.fileUploadStatus === 'FAILED';
        }
        let validationText = this.generateValidationText(currentFile, false);
        if (currentFile.fileUploadStatus !== 'IGNORED') {
          subItems.push({
            ...currentFile,
            validationText,
            payload: currentFile?.payload ? this.translate(`payloadLocales.${currentFile?.payload}`) : '',
            targetLanguageName: currentFile.targetLanguageName?.split('[')[0].trim(),
            isSubItem: true
          });
          if (samePayload) {
            if (oldPayload) {
              samePayload = oldPayload === currentFile.payload;
            }
            oldPayload = currentFile.payload;
          }
        }
        j++;
      }
      let payload = '';
      if (samePayload) {
        if (currentFile?.payload) {
          payload = this.translate(`payloadLocales.${currentFile?.payload}`);
        }
      } else {
        payload = this.translate('payloadLocales.MULTIPLE');
      }
      let documentStatus = haveSomeFailed ? 'FAILED' : currentFile?.fileUploadStatus;
      let status = documentStatus?.toLowerCase();
      newItem = {
        ...currentFile,
        fileName: currentFile?.documentName || currentFile?.fileName,
        expanded: documentStatus === 'FAILED',
        fileUploadStatus: documentStatus,
        payload: payload,
        validationText: _.startCase(status),
        targetLanguageName: currentFile?.targetLanguageName?.split('[')[0].trim(),
        subItems: subItems
      };

      if (status) {
        (results as any)[status].push(newItem);
      }
      i++;
    }

    return results;
  }

  /**
   * @override
   */
  updateParamsForPreconditionCheck = (params: any) => {
    if (!params.phaseName) {
      let { files } = this.state;
      let { uploaded } = files;
      let phaseName = uploaded[0]?.phaseName;
      params = {
        ...params,
        phaseName
      };
    }

    return params;
  }

  renderBody() {
    const {
      activeTabIndex,
      files,
      tabCount
    } = this.state;

    return (
      <>
        <PuzzleTabStrip
          className={'tp-upload-report-wrapper'}
          handleSelect={this.onTabSelect}
          tabCount={tabCount}
          translate={this.translate}
          selected={activeTabIndex}
        >
          <TabStrip selected={activeTabIndex}>
            <TabStripTab
              title={
                <div className={'tp-tabstrip-tab-title'}>
                  <span
                    className={'tp-tabstrip-title-files-count'}>{this.translate('uploaded', { count: this.countFiles(files.uploaded) })}</span>
                </div>
              }
              contentClassName={'tp-tab-uploaded'}
            >
              <div className={'tp-tab-text'}>
                {this.translate('tabUploadText')}
              </div>
              <FileList files={files.uploaded} activeDialog={this.props.activeDialog} />
            </TabStripTab>
            <TabStripTab
              title={
                <div className={'tp-tabstrip-tab-title'}>
                  <span
                    className={'tp-tabstrip-title-files-count'}>{this.translate('failed', { count: this.countFiles(files.failed) })}</span>
                </div>
              }
              contentClassName={'tp-tab-failed'}
            >
              <div className={'tp-tab-text'}>
                {this.translate('tabFailedText')}
              </div>
              <FileList files={files.failed} activeDialog={this.props.activeDialog} />
            </TabStripTab>
            <TabStripTab
              title={
                <div className={'tp-tabstrip-tab-title'}>
                  <span
                    className={'tp-tabstrip-title-files-count'}>{this.translate('ignored', { count: this.countFiles(files.ignored) })}</span>
                </div>
              }
              contentClassName={'tp-tab-ignored'}
            >
              <div className={'tp-tab-text'}>
                {this.translate('tabIgnoredText')}
              </div>
              <FileList files={files.ignored} activeDialog={this.props.activeDialog} />
            </TabStripTab>
          </TabStrip>
        </PuzzleTabStrip>
      </>
    )
  }
}

export default ReportDialog;
